import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  Divider,
  Grid,
  Checkbox,
  FormControlLabel,
  Stack,
  Card,
  Tooltip,
  Select,
  MenuItem
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { toast } from 'react-toastify';
import store from '../../../global/config/store.ts';
import { setLoadingFalse, setLoadingTrue } from '../../../global/config/Action/loadingActions.ts';

import {
  ApiGetQuizQuestions,
  ApiPostQuizQuestion,
  ApiPutQuizQuestion,
  ApiQuizreTake,
  ApiDeleteQuizQuestion,
  ApiRetakeQuiz
} from '../../../apiService/Core/Apicall.tsx';
import { CheckBox, CheckBoxOutlineBlank, CheckBoxOutlined, RadioButtonChecked } from '@mui/icons-material';
import PatchedReactQuill from '../ManageVenue/PatchedReactQuill.tsx';
import Editor from '../ManageVenue/Editor.tsx';

interface Option {
  option_id: number;
  option_label: string;
}

interface Question {
  id: number;
  text: string;
  options: Option[];
  correctAnswer: string;
  correctAnswers: any[];
  description: string;
  questionType: 'radio' | 'checkbox'
}

interface QuizSectionProps {
  type: 'business' | 'venue';
  id: number;
}

const QuizSection: React.FC<QuizSectionProps> = ({ type, id }) => {
  const currentUserId = Number(localStorage.getItem('userId') || localStorage.getItem('createdBy') || 0);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [questiontype, setQuestionType] = useState<"checkbox" | "radio">('radio');
  const [searchTerm, setSearchTerm] = useState('');

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [questionText, setQuestionText] = useState('');
  const [options, setOptions] = useState<Option[]>([]);
  const [correctAnswer, setCorrectAnswer] = useState('');
  const [correctAnswerArray, setCorrectAnswerArray] = useState<any>([]);
  const [description, setDescription] = useState('');
  const [editingQuestionId, setEditingQuestionId] = useState<number | null>(null);

  const parseOptions = (optionsVal: any): Option[] => {
    if (typeof optionsVal === 'string') {
      try {
        return JSON.parse(optionsVal);
      } catch (e) {
        console.error('Error parsing options:', e);
        return [];
      }
    }
    if (Array.isArray(optionsVal)) {
      return optionsVal;
    }
    return [];
  };

  const fetchQuestions = async () => {
    if (!id) return;
    try {
      store.dispatch(setLoadingTrue());
      const queryParam = type === 'business'
        ? `?type=business&clientEntityId=${id}`
        : `?type=venue&venueId=${id}`;

      const response = await ApiGetQuizQuestions(queryParam);
      const questionsData = response?.data || [];

      const parsedQuestions: Question[] = questionsData.map((item: any) => ({
        id: item.quiz_question_id,
        text: item.question_text,
        options: parseOptions(item.options),
        correctAnswer: item.questionType != 'checkbox' ? (item.answerIds?.toString() || item.answer.toString()) : null,
        correctAnswers: item.questionType == 'checkbox' ? item.answerIds.split(",") : [],
        description: item.questionDescription || '',
        questionType: item.questionType || 'radio'
      }));

      setQuestions(parsedQuestions);
    } catch (error) {
      console.error('Failed to fetch quiz questions:', error);
      toast.error('Failed to load quiz questions');
    } finally {
      store.dispatch(setLoadingFalse());
    }
  };

  useEffect(() => {
    fetchQuestions();
  }, [id, type]);

  const handleAddOption = () => {
    const newId = options.length > 0 ? Math.max(...options.map(o => o.option_id)) + 1 : 1;
    setOptions([...options, { option_id: newId, option_label: '' }]);
  };

  // const handleRemoveOption = (index: number) => {
  //   const newOptions = [...options];
  //   newOptions.splice(index, 1);
  //   setOptions(newOptions);
  // };
  const handleRemoveOption = (index: number) => {
    const optionToRemove = options[index];

    // Remove the option
    const newOptions = [...options];
    newOptions.splice(index, 1);
    setOptions(newOptions);

    // Remove from selected radio answer
    if (correctAnswer === optionToRemove.option_id.toString()) {
      setCorrectAnswer("");
    }

    // Remove from selected checkbox answers
    setCorrectAnswerArray((prev: any) =>
      prev.filter((id: any) => id !== optionToRemove.option_id.toString())
    );
  };

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index].option_label = value;
    setOptions(newOptions);
  };

  const handleEditQuestion = (question: Question) => {
    setEditingQuestionId(question.id);
    setQuestionText(question.text);
    setOptions(question.options.map(opt => ({ ...opt })));
    setCorrectAnswer(question.correctAnswer);
    setCorrectAnswerArray(question.correctAnswers);
    setDescription(question.description);
    setQuestionType(question.questionType)
    setIsDialogOpen(true);
  };

  const handleDeleteQuestion = async (questionId: number) => {
    const isConfirmed = window.confirm('Are you sure you want to delete this question?');
    if (!isConfirmed) return;

    try {
      store.dispatch(setLoadingTrue());
      const payload = {
        quiz_question_id: questionId
      };
      await ApiDeleteQuizQuestion(payload);
      toast.success('Question deleted successfully');
      fetchQuestions();
    } catch (error: any) {
      console.error('Error deleting question:', error);
      toast.error(error || 'Failed to delete question');
    } finally {
      store.dispatch(setLoadingFalse());
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingQuestionId(null);
    resetForm();
  };

  const resetForm = () => {
    setQuestionText('');
    setOptions([{ option_id: 1, option_label: '' }]);
    setCorrectAnswer('');
    setDescription('');
  };

  const handleSubmit = async () => {
    if (!questionText.trim()) {
      toast.warning('Question text is required');
      return;
    }

    if (options.length === 0 || options.some(option => !option.option_label.trim())) {
      toast.warning('All options must have content');
      return;
    }

    if (!correctAnswer && questiontype === 'radio') {
      toast.warning('Please select the correct answer');
      return;
    }
    if (!correctAnswerArray && questiontype === 'checkbox') {
      toast.warning('Please select atleast one answer');
      return;
    }

    const questionData: any = {
      question_text: questionText,
      options: options,
      answer: questiontype === 'checkbox' ? 0 : parseInt(correctAnswer, 10),
      questionDescription: normalizeListHtml(description),
      questionType: questiontype,
      answerIds: questiontype === 'checkbox' ? correctAnswerArray?.join(',') : correctAnswer
    };
    try {
      store.dispatch(setLoadingTrue());
      if (editingQuestionId) {
        questionData.quiz_question_id = editingQuestionId;
        questionData.modifiedBy = currentUserId;
        await ApiPutQuizQuestion(questionData);
        toast.success('Question updated successfully');
      } else {
        questionData.type = type;
        if (type === 'business') {
          questionData.clientEntityId = id;
        } else {
          questionData.venueId = id;
        }
        questionData.createdBy = currentUserId;
        await ApiPostQuizQuestion(questionData);
        toast.success('Question added successfully');
      }

      await fetchQuestions();
      handleCloseDialog();
    } catch (error: any) {
      console.error('Error submitting question:', error);
      toast.error(error || 'Failed to submit question');
    } finally {
      store.dispatch(setLoadingFalse());
    }
  };
  const handleMarkRetake = async () => {
    const isConfirmed = window.confirm(
      'Are you sure you want to mark this quiz as retake required? This forces shoppers to retake the quiz for their next job.'
    );
    if (!isConfirmed) return;

    try {
      store.dispatch(setLoadingTrue());
      const payload: any = {
        type: type,
        retakeRequired: true
      };

      if (type === 'business') {
        payload.clientEntityId = id;
      } else {
        payload.venueId = id;
      }

      // const response = await ApiQuizreTake(payload);
      const response = await ApiRetakeQuiz(payload);
      toast.success(response?.message || 'Retake marked successfully');
    } catch (error: any) {
      console.error('Error marking retake:', error);
      toast.error(error || 'Failed to mark retake');
    } finally {
      store.dispatch(setLoadingFalse());
    }
  };

  const filteredQuestions = questions.filter(q =>
    q.text.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCheckboxAnswer = (optionId: any) => {
    setCorrectAnswerArray((prev: any) =>
      prev.includes(optionId.toString())
        ? prev?.filter((id) => id !== optionId.toString())
        : [...prev, optionId.toString()]
    );
  };


  const handleEditorValueChange = (val: any) => {
    setDescription(val);
  };

  const normalizeListHtml = (html: string): string => {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, "text/html")

    // Convert Quill list structure to semantic UL/OL
    const listItems = doc.querySelectorAll("li")

    listItems.forEach((li) => {
      const listType = li.getAttribute("data-list")
      const parent = li.parentElement

      if (!parent || !listType) return

      // Remove Quill-specific attribute
      li.removeAttribute("data-list")

      if (listType === "bullet") {
        if (parent.tagName.toLowerCase() === "ol") {
          const ul = doc.createElement("ul")

          // Copy existing attributes if any
          Array.from(parent.attributes).forEach((attr) => {
            ul.setAttribute(attr.name, attr.value)
          })

          parent.parentNode?.replaceChild(ul, parent)

          // Move all li elements into UL
          Array.from(parent.children).forEach((child) => {
            ul.appendChild(child)
          })
        }
      }

      if (listType === "ordered") {
        if (parent.tagName.toLowerCase() === "ul") {
          const ol = doc.createElement("ol")

          Array.from(parent.attributes).forEach((attr) => {
            ol.setAttribute(attr.name, attr.value)
          })

          parent.parentNode?.replaceChild(ol, parent)

          Array.from(parent.children).forEach((child) => {
            ol.appendChild(child)
          })
        }
      }
    })

    // Remove any remaining Quill-specific list attributes/classes
    doc.querySelectorAll("li").forEach((li) => {
      li.removeAttribute("data-list")
      li.classList.remove("ql-indent-1", "ql-indent-2", "ql-indent-3")
    })

    return doc.body.innerHTML
  }

  return (
    <Box sx={{ mt: 5, pb: 4 }}>
      <Divider sx={{ my: 4, borderColor: '#EBE8DF' }} />

      {/* Retake Notice Box */}
      <Box
        sx={{
          bgcolor: '#FDFCF9',
          border: '1px dashed #75724C4D',
          borderRadius: '20px',
          p: 3,
          mb: 4,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', maxWidth: '75%' }}>
          <InfoIcon sx={{ color: '#75724C', mt: 0.25 }} />
          <Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, color: '#75724C', textTransform: 'uppercase', mb: 0.5, fontSize: '11px', letterSpacing: '1px' }}
            >
              Update Quiz Requirement
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px', lineHeight: 1.4 }}>
              If you have updated brief instructions or quiz questions, click "Retake Quiz Required". Shoppers who have completed jobs previously will be required to take this new quiz before apply their next job.
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          onClick={handleMarkRetake}
          sx={{
            textTransform: 'none',
            borderRadius: '20px',
            px: 3,
            py: 1,
            fontWeight: 'bold',
            bgcolor: '#75724C',
            color: 'white',
            '&:hover': { bgcolor: '#5E5B3D' }
          }}
        >
          Retake Quiz Required
        </Button>
      </Box>

      {/* Quiz List Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase', mb: 0.5, display: 'block' }}
          >
            QUIZ QUESTIONS
          </Typography>
          <Typography className="fw-bold" variant="h4" sx={{ fontSize: '24px' }}>
            {questions.length} {questions.length === 1 ? 'question' : 'questions'}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            resetForm();
            setIsDialogOpen(true);
          }}
          sx={{
            textTransform: 'none',
            borderRadius: '20px',
            px: 3,
            py: 1,
            fontWeight: 'bold',
            bgcolor: 'black',
            color: 'white',
            '&:hover': { bgcolor: '#222' }
          }}
        >
          Add Question
        </Button>
      </Box>

      {/* Search Field */}
      <TextField
        fullWidth
        placeholder="Search your Question here..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{
          mb: 3,
          '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            bgcolor: 'white',
            border: '1px solid #EBE8DF',
            '& fieldset': { border: 'none' },
            '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }
          }
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: '#75724C' }} />
            </InputAdornment>
          )
        }}
      />

      {/* Questions Stack */}
      <Stack spacing={2}>
        {filteredQuestions.length > 0 ? (
          filteredQuestions.map((question, index) => {
            const displayNum = String(index + 1).padStart(2, '0');
            return (
              <Card
                key={question.id}
                elevation={0}
                sx={{
                  border: '1px solid #EBE8DF',
                  borderRadius: '20px',
                  p: 3,
                  bgcolor: 'white',
                  transition: 'box-shadow 0.2s',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.04)'
                  }
                }}
              >
                {/* Header of Question Card */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                      {displayNum}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#75724C',
                        fontWeight: 700,
                        letterSpacing: '1px',
                        textTransform: 'uppercase',
                        bgcolor: '#75724C1A',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '12px'
                      }}
                    >
                      Question ID: #{question.id}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEditQuestion(question)}
                      sx={{ color: '#75724C', '&:hover': { bgcolor: '#F5F4F0' } }}
                    >
                      <EditIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteQuestion(question.id)}
                      sx={{ color: 'error.main', '&:hover': { bgcolor: '#FFEBEE' } }}
                    >
                      <DeleteIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                </Box>

                <Typography
                  variant="body1"
                  sx={{ fontWeight: 700, color: '#1A1A1A', fontSize: '15px', lineHeight: 1.4, mb: 2 }}
                >
                  Q: {question.text}
                </Typography>

                {/* Options Grid */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {question.options.map((opt, optIndex) => {
                    const isCorrect = question?.questionType === 'radio' ? question.correctAnswer === opt.option_id.toString() : question?.correctAnswers?.includes(opt.option_id.toString());
                    return (
                      <Grid item xs={12} sm={6} key={opt.option_id}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: '12px',
                            border: isCorrect ? '1px solid #75724C' : '1px solid #EBE8DF',
                            bgcolor: isCorrect ? '#75724C0A' : '#FDFDFD',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5
                          }}
                        >{question?.questionType === "radio" ? (
                          isCorrect ? (
                            <CheckCircleIcon sx={{ color: "#75724C", fontSize: "20px" }} />
                          ) : (
                            <RadioButtonUncheckedIcon sx={{ color: "#C0BEB2", fontSize: "20px" }} />
                          )
                        ) : (
                          isCorrect ? (
                            <CheckBox sx={{ color: "#75724C", fontSize: "20px" }} />
                          ) : (
                            <CheckBoxOutlineBlank sx={{ color: "#C0BEB2", fontSize: "20px" }} />
                          )
                        )}
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: isCorrect ? 600 : 500,
                              color: isCorrect ? '#75724C' : 'text.primary',
                              fontSize: '13.5px'
                            }}
                          >
                            {optIndex + 1}. {opt.option_label}
                          </Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>

                {/* Description */}
                {question.description && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: '#F9F9F8',
                      borderRadius: '10px',
                      borderLeft: '4px solid #75724C33'
                    }}
                  >
                    <Typography variant="caption" sx={{ color: '#75724C', fontWeight: 700, display: 'block', mb: 0.5 }}>
                      EXPLANATION
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12.5px', lineHeight: 1.4 }}>
                      <Box className='venue-description' dangerouslySetInnerHTML={{ __html: question.description }} />
                      {/* {question.description} */}
                    </Typography>
                  </Box>
                )}
              </Card>
            );
          })
        ) : (
          <Box sx={{ bgcolor: 'white', border: '1px solid #EBE8DF', borderRadius: '20px', p: 6, textAlign: 'center' }}>
            <Typography variant="body1" color="textSecondary">
              No questions added yet for this quiz. Click "Add Question" to get started.
            </Typography>
          </Box>
        )}
      </Stack>

      {/* Dialog for Add/Edit Question */}
      <Dialog open={isDialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
            {editingQuestionId ? 'Edit Question' : 'Add Question'}
          </Typography>
          <IconButton onClick={handleCloseDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ maxHeight: '65vh', overflowY: 'auto', p: 3 }}>
          <Stack spacing={3}>
            {/* Question Text */}
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', display: 'block', mb: 1 }}>
                Question Text *
              </Typography>
              <TextField
                multiline
                rows={3}
                fullWidth
                value={questionText}
                onChange={(e) => setQuestionText(e.target.value)}
                placeholder="Enter your question here..."
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
            </Box>

            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', display: 'block', mb: 1 }}>
                Question Type *
              </Typography>
              <Select
                sx={{ borderRadius: '10px' }}
                value={questiontype}
                // onChange={(e: any) => setQuestionType(e.target.value)}
                onChange={(e: any) => {
                  setQuestionType(e.target.value);
                  setCorrectAnswer("");
                  setCorrectAnswerArray([]);
                }}
                size="small"
                fullWidth
              >
                <MenuItem value="checkbox">Checkbox</MenuItem>
                <MenuItem value="radio">Radio</MenuItem>
              </Select>
            </Box>
            {/* Options Management */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase' }}>
                  Options *
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddOption}
                  sx={{ color: '#75724C', textTransform: 'none', fontWeight: 'bold' }}
                >
                  Add Option
                </Button>
              </Box>

              <Stack spacing={2}>
                {options.map((option, index) => (
                  <Box key={option.option_id} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    {/* Correct answer indicator */}
                    <Tooltip title="Mark as correct answer">
                      <IconButton
                        // onClick={() => setCorrectAnswer(option.option_id.toString())}
                        onClick={() => {
                          if (questiontype === "radio") {
                            setCorrectAnswer(option.option_id.toString());
                          } else {
                            handleCheckboxAnswer(option.option_id);
                          }
                        }}
                        sx={{
                          color:
                            questiontype === "radio"
                              ? correctAnswer === option.option_id.toString()
                                ? "#75724C"
                                : "#C0BEB2"
                              : correctAnswerArray.includes(option.option_id.toString())
                                ? "#75724C"
                                : "#C0BEB2",
                          '&:hover': { bgcolor: '#F5F4F0' }
                        }}
                      >
                        {questiontype === "radio" ? (
                          correctAnswer === option.option_id.toString() ? (
                            <RadioButtonChecked />
                          ) : (
                            <RadioButtonUncheckedIcon />
                          )
                        ) : correctAnswerArray.includes(option.option_id.toString()) ? (
                          <CheckBox />
                        ) : (
                          <CheckBoxOutlineBlank />
                        )}
                      </IconButton>
                    </Tooltip>

                    {/* Option Text field */}
                    <TextField
                      fullWidth
                      value={option.option_label}
                      onChange={(e) => handleOptionChange(index, e.target.value)}
                      placeholder={`Option ${index + 1}`}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                    />

                    {/* Remove Option button */}
                    {options.length > 1 && (
                      <IconButton
                        onClick={() => handleRemoveOption(index)}
                        sx={{ color: 'error.main', '&:hover': { bgcolor: '#FFEBEE' } }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                ))}
              </Stack>
            </Box>

            {/* Description / Explanation */}
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', display: 'block', mb: 1 }}>
                Question Explanation
              </Typography>
              {/* <PatchedReactQuill
                setEditorValue={handleEditorValueChange}
                editorValue={description}
                isUpdate={false}
              /> */}
              <Editor
                setValue={handleEditorValueChange}
                value={description}
                isUpdate={false}
              />
              {/* <TextField
                multiline
                rows={2}
                fullWidth
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter description or correct explanation (optional)"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              /> */}
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2.5 }}>
          <Button variant="outlined" onClick={handleCloseDialog} sx={{ color: '#75724C', borderColor: '#75724C', textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            className="table-row-background"
            sx={{ textTransform: 'none' }}
          >
            {editingQuestionId ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuizSection;
